import ufeApi from 'services/api/ufeApi';
import urlUtils from 'utils/Url';
import userUtils from 'utils/User';
import { gqlClient } from 'services/api/gql/gqlClient';
import { PurchaseHistory } from 'constants/gql/queries/profile/purchaseHistory.gql';

// https://jira.sephora.com/wiki/display/ILLUMINATE/Get+Purchase+History

/** @returns {boolean} Whether OIS purchase history migration is enabled */
const isOisEnabled = () =>
    !!(Sephora?.configurationSettings?.oisMigration?.global?.isEnabled &&
        Sephora?.configurationSettings?.oisMigration?.purchaseHistory?.isEnabled);

// Explicit OXS-option-code → OIS-enum maps. Mirrors the verified native channels
// (Android PurchaseHistoryInputBuilder / iOS enums). A naive camelCase→SNAKE_CASE
// conversion is unsafe here: codes such as 'brandNameASC' would become
// 'BRAND_NAME_A_S_C'. Unknown values resolve to undefined so the OIS server applies
// its own defaults (sortBy = RECENTLY, groupBy = STORE_AND_DATE).
// sortBy codes: components/Product/ProductSort/ProductSortOptions.js
const SORT_BY_ENUM = {
    recently: 'RECENTLY',
    brandNameASC: 'BRAND_NAME_ASC',
    brandNameDESC: 'BRAND_NAME_DESC',
    priceLowToHigh: 'PRICE_LOW_TO_HIGH',
    priceHighToLow: 'PRICE_HIGH_TO_LOW'
};
const GROUP_BY_ENUM = {
    none: 'NONE',
    storeAndDate: 'STORE_AND_DATE',
    frequencyAndDate: 'FREQUENCY_AND_DATE'
};
// purchaseFilter codes: components/RichProfile/PurchaseHistoryList/PurchasesFilter/PurchasesFilterOptions.js
const PURCHASE_FILTER_ENUM = {
    all: 'BOTH',
    both: 'BOTH',
    online: 'ONLINE',
    store: 'STORE',
    rewards: 'REWARDS'
};

/** @param {string|undefined} value @returns {string|undefined} OIS PurchaseSortBy enum */
export const toSortByEnum = value => SORT_BY_ENUM[value];

/** @param {string|undefined} value @returns {string|undefined} OIS PurchaseGroupBy enum */
export const toGroupByEnum = value => GROUP_BY_ENUM[value];

/** @param {string|undefined} value @returns {string|undefined} OIS PurchaseFilter enum */
export const toPurchaseFilterEnum = value => PURCHASE_FILTER_ENUM[value];

/**
 * Fetches a single page of purchase history via OIS (GraphQL) or OXS (REST).
 * @param {string} profileId - BI profile identifier
 * @param {Object} [options] - Query options (sortBy, itemsPerPage, currentPage, groupBy, etc.)
 * @returns {Promise<Object>} Purchase history response with purchasedItems and purchasedGroups
 */
const getPurchaseHistory = async (profileId, options = {}) => {
    const biAccountId = userUtils.getBiAccountId();
    const nonBiAccountResponse = {
        activities: [],
        meta: { totalItems: 0 },
        purchasedGroups: [],
        purchasedItemsCount: 0,
        purchasedItems: []
    };

    const {
        sortBy, itemsPerPage, currentPage, groupBy, excludeSamples,
        purchaseFilter, excludeRewards, includeNonBiUserPurchases
    } = options;

    const isRewardsFilter = purchaseFilter === 'rewards';

    // Rewards path always uses OXS — OIS does not cover the redemption-history endpoint.
    if (!isRewardsFilter && isOisEnabled()) {
        // locale is intentionally omitted: PurchaseHistoryInput has no locale field;
        // the OIS server derives it from the request's site_locale/site_language cookies.
        const data = await gqlClient.query({
            operationName: 'PurchaseHistory',
            query: PurchaseHistory,
            variables: {
                input: {
                    profileId,
                    sortBy: toSortByEnum(sortBy),
                    itemsPerPage,
                    currentPage,
                    groupBy: toGroupByEnum(groupBy),
                    excludeSamples,
                    purchaseFilter: toPurchaseFilterEnum(purchaseFilter),
                    excludeRewards,
                    includeNonBiUserPurchases
                }
            }
        });

        const result = data?.purchaseHistory;

        if (result?.responseCode && result.responseCode !== 'SUCCESS') {
            const error = new Error(result.message || 'Get purchase history failed');
            error.errorCode = -1;
            error.responseCode = result.responseCode;
            error.errorMessages = result.message ? [result.message] : [];
            throw error;
        }

        if (!result) {
            const error = new Error('Get purchase history failed — unexpected response shape');
            error.errorCode = -1;
            error.responseCode = 'UNEXPECTED_RESPONSE';
            error.errorMessages = ['purchaseHistory field missing from GraphQL response'];
            throw error;
        }

        // groupBy: NONE returns a flat `purchasedItems` list with `purchasedGroups: null`;
        // grouped modes populate `purchasedGroups`. Prefer grouped items, fall back to flat.
        const groupedItems = (result.purchasedGroups || [])
            .flatMap(group => group.purchasedItems || []);
        const purchasedItems = groupedItems.length ? groupedItems : (result.purchasedItems || []);

        return {
            ...result,
            purchasedItems,
            purchasedItemsCount: result.purchasedItemsCount ?? purchasedItems.length
        };
    }

    if (isRewardsFilter && !biAccountId) {
        return nonBiAccountResponse;
    }

    const oxsUrl = isRewardsFilter
        ? `/gway/v1/lxs/${biAccountId}/redemption-history`
        : `/api/bi/profiles/${profileId}/purchases`;

    const qsParams = {
        sortBy,
        itemsPerPage,
        currentPage,
        groupBy,
        excludeSamples,
        purchaseFilter,
        excludeRewards,
        includeNonBiUserPurchases
    };
    const qs = urlUtils.makeQueryString(qsParams);
    const url = qs ? `${oxsUrl}?${qs}` : oxsUrl;

    return ufeApi.makeRequest(url, { method: 'GET', headers: { 'x-requested-source': 'web' } });
};

export default getPurchaseHistory;
